import { StationState, RootState } from '@/store/types'
import { moduleActionContext } from '@/store'
import { ActionContext } from 'vuex'
import { defineModule } from 'direct-vuex'
import { DeepPartial, KeyDict } from '@/types'
import { CollectionNames } from '@/modules/database/utils'
import fb from '@/firebase'
import { Station } from '../utils/station'
import firebase from 'firebase/app'
import Vue from 'vue'
import Convertable from '@/classes/Convertable'
import studio from '@/modules/database/utils'
import { Result, ResultVoid } from '@/modules/errorHandling/results'
import { MergeOption } from '@/modules/database/utils/database'
import { cloneDeep } from 'lodash'
import { FirestoreDocSnapper, FirestoreQuerySnapper,
         FirestoreSnapperType } from '@/modules/database/utils/databaseQuerySubscriptions'

type StationContext = ActionContext<StationState, RootState>

const stationModule = defineModule({
  namespaced: true,
  state: {
    currentStationId: null,
    stations: {}
  } as StationState,
  getters: {
    currentStation (state: StationState): Station | null {
      return state.currentStationId ? state.stations[state.currentStationId] ?? null : null
    }
  },
  actions: {
    async subscribe (context: StationContext, teamId: string) {
      const { commit } = stationModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection(CollectionNames.STATION).where('team_id', '==', teamId).where('deleted', '==', false),
        mutation: commit.setStations,
        type: FirestoreSnapperType.Query
      }
      await studio.subscriptions.subscribeFB(`stations_${teamId}`, snapper)
    },
    async unsubscribe (context: StationContext, teamId: string) {
      const { commit } = stationModuleActionContext(context)
      studio.subscriptions.unsubscribeFB(`stations_${teamId}`, commit.clearStations)
    },
    async subscribeCurrentStation (context: StationContext, stationId: string) {
      const { commit } = stationModuleActionContext(context)
      commit.setCurrentStationId(stationId)
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection(CollectionNames.STATION).doc(stationId),
        mutation: commit.setStation,
        type: FirestoreSnapperType.Document
      }
      await studio.subscriptions.subscribeFB(`station_${stationId}`, snapper)
    },
    async unsubscribeCurrentStation (context: StationContext, stationId: string) {
      const { commit } = stationModuleActionContext(context)
      studio.subscriptions.unsubscribeFB(`station_${stationId}`, commit.clearStations)
    },
    async add (_context: StationContext, station: Station): Promise<Result<string, 'database'>> {
      try {
        const doc = await fb.db.collection(CollectionNames.STATION).add(Convertable.toObject(station))
        return Result.success(doc.id)
      } catch (error) {
        if (error instanceof Error) {
          return Result.fail('database', error.message)
        }
      }
      return Result.fail('database', 'Unknown error')
    },
    async delete (_context: StationContext, stationId: string): Promise<ResultVoid<'database'>> {
      const result = await studio.db.collection(CollectionNames.STATION).doc(stationId).set({
        deleted: true
      }, MergeOption.MERGE)
      if (!result.isSuccess) return Result.fail('database', result.message)
      return Result.ok()
    },
    async update (_context: StationContext,
                  stationUpdate: DeepPartial<Station> & { id: string }): Promise<ResultVoid<'database'>> {
      const id = stationUpdate.id
      const update: DeepPartial<Station> & { id?: string} = cloneDeep(stationUpdate)
      delete update.id
      const result = await studio.db.collection(CollectionNames.STATION).doc(id).set(update, MergeOption.MERGE)
      if (!result.isSuccess) {
        return Result.fail('database', result.message)
      }
      return Result.ok()
    }
  },
  mutations: {
    setStations (state: StationState, snap: firebase.firestore.QuerySnapshot) {
      const stations: KeyDict<Station> = {}
      snap.docs.forEach((doc) => {
        const station = Station.load(doc.data())
        if (station.isSuccess) {
          stations[doc.id] = station.value
        }
      })
      Vue.set(state, 'stations', stations)
    },
    clearStations (state: StationState) {
      state.stations = {}
    },
    setStation (state: StationState, snap: firebase.firestore.DocumentSnapshot) {
      const station = Station.load(snap.data())
      if (station.isSuccess) {
        Vue.set(state.stations, snap.id, station.value)
      }
    },
    clearCurrentStation (state: StationState) {
      state.currentStationId = null
    },
    setCurrentStationId (state: StationState, stationId: string) {
      state.currentStationId = stationId
    }
  }
})

export default stationModule
export const stationModuleActionContext = (context: StationContext) => moduleActionContext(context, stationModule)
